import React, { useState, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Avatar,
  Fab,
  Menu,
  MenuItem,
  Alert,
  Breadcrumbs,
  Link,
  Divider,
  Stack,
  TextField,
  CircularProgress,
  Snackbar,
  Switch,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add,
  Edit,
  Visibility,
  Delete,
  FileCopy,
  MoreVert,
  Assignment,
  People,
  CalendarToday,
  Email,
  Image as ImageIcon,
  CheckCircle,
  TrendingUp,
  Send,
  Star,
  Share,
  Group,
  Lock,
  Description,
  EventNote
} from '@mui/icons-material';
import { UseApi } from '../Hooks/UseApi';
import CustomFormBuilder from './CustomFormBuilder';
import CustomFormRenderer from './CustomFormRenderer';
import IntakeFormBuilder from './IntakeFormBuilder';

const CustomFormManager = () => {
  const api = UseApi();
  const apiRef = useRef(null);
  // Keep apiRef updated with latest api on every render
  apiRef.current = api;
  
  const { user, getAccessTokenSilently } = useAuth0();
  const namespace = 'https://inkedupmanager.com';
  
  const [templates, setTemplates] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState('dashboard');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuTemplate, setMenuTemplate] = useState(null);
  const [accessTokenRoles, setAccessTokenRoles] = useState([]);
  
  // NEW: Tab state for Consent vs Intake forms
  const [activeTab, setActiveTab] = useState(0);
  
  // Aftercare email states
  const [showAftercareDialog, setShowAftercareDialog] = useState(false);
  const [aftercareInstructions, setAftercareInstructions] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [emailError, setEmailError] = useState('');

  // Studio sharing states
  const [isStudioOwner, setIsStudioOwner] = useState(false);
  const [sharingTemplate, setSharingTemplate] = useState(null);
  const [showSharingDialog, setShowSharingDialog] = useState(false);

  // Track if initial load has been done
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    // Prevent multiple initial loads
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;
    
    loadTemplates();
    loadSubmissions();
    getRolesFromToken();
    checkStudioOwnership();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getRolesFromToken = async () => {
    try {
      const token = await getAccessTokenSilently();
      const tokenPayload = JSON.parse(atob(token.split('.')[1]));
      const roles = tokenPayload[`${namespace}/roles`] || [];
      setAccessTokenRoles(roles);
    } catch (error) {
      console.error('Error getting access token roles:', error);
      const idTokenRoles = user?.[`${namespace}/roles`] || [];
      setAccessTokenRoles(idTokenRoles);
    }
  };

  const checkStudioOwnership = async () => {
    try {
      const studioInfo = await apiRef.current.fetchStudioMembers();
      if (studioInfo && studioInfo.user_role === 'owner') {
        setIsStudioOwner(true);
      }
    } catch (error) {
      setIsStudioOwner(false);
    }
  };

  const hasEmailAccess = accessTokenRoles.includes('standard_subscriber') ||
                        accessTokenRoles.includes('growth_studio_subscriber');

  const loadTemplates = async () => {
    try {
      const response = await apiRef.current.fetchCustomFormTemplates();
      const templatesData = response?.templates || response || [];
      setTemplates(templatesData);
    } catch (error) {
      console.error('Error loading templates:', error);
      setTemplates([]);
    }
  };

  const loadSubmissions = async () => {
    try {
      const response = await apiRef.current.fetchCustomFormSubmissions();
      const submissionsData = response?.submissions || response || [];
      setSubmissions(submissionsData);
    } catch (error) {
      console.error('Error loading submissions:', error);
      setSubmissions([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter templates by type
  const consentTemplates = templates.filter(t => (t.form_type || 'consent') === 'consent');
  const intakeTemplates = templates.filter(t => t.form_type === 'intake');

  const handleToggleSharing = async (template, shareWithStudio) => {
    try {
      setSharingTemplate(template.id);
      const response = await apiRef.current.toggleTemplateSharing(template.id, shareWithStudio);
      
      if (response.studio_shareable !== undefined) {
        setTemplates(templates.map(t => 
          t.id === template.id 
            ? { ...t, studio_shareable: response.studio_shareable, is_template: response.is_template }
            : t
        ));
        const action = shareWithStudio ? 'enabled' : 'disabled';
      }
    } catch (error) {
      console.error('Error toggling template sharing:', error);
      alert('Error updating sharing settings. Please try again.');
    } finally {
      setSharingTemplate(null);
      setShowSharingDialog(false);
    }
  };

  const handleSendAftercareEmail = async (submission) => {
    setSendingEmail(true);
    setEmailError('');
    
    try {
      const response = await apiRef.current.sendAftercareEmail(submission.client_id, {
        submission_id: submission.id,
        aftercare_instructions: aftercareInstructions || 'Standard aftercare instructions'
      });
      
      if (response.success) {
        setEmailSuccess(true);
        setSubmissions(submissions.map(s => 
          s.id === submission.id ? { ...s, aftercare_email_sent: true } : s
        ));
      } else {
        throw new Error(response.error || 'Failed to send email');
      }
    } catch (error) {
      console.error('Error sending aftercare email:', error);
      setEmailError(error.message || 'Failed to send aftercare email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleMenuOpen = (event, template) => {
    setAnchorEl(event.currentTarget);
    setMenuTemplate(template);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuTemplate(null);
  };

  // UPDATED: Create template based on active tab
  const handleCreateTemplate = () => {
    setSelectedTemplate(null);
    if (activeTab === 0) {
      setCurrentView('create'); // Consent form builder
    } else {
      setCurrentView('create-intake'); // Intake form builder
    }
  };

  // UPDATED: Edit template based on form_type
  const handleEditTemplate = (template) => {
    setSelectedTemplate(template);
    if (template.form_type === 'intake') {
      setCurrentView('edit-intake');
    } else {
      setCurrentView('edit');
    }
    handleMenuClose();
  };

  const handleFillTemplate = async (template) => {
    try {
      const freshTemplate = await apiRef.current.getCustomFormTemplate(template.id);
      setSelectedTemplate(freshTemplate);
      setCurrentView('fill');
      handleMenuClose();
    } catch (error) {
      console.error('Error fetching fresh template data:', error);
      const fallbackTemplate = templates.find(t => t.id === template.id) || template;
      setSelectedTemplate(fallbackTemplate);
      setCurrentView('fill');
      handleMenuClose();
    }
  };

  const handleViewSubmissions = (template = null) => {
    setSelectedTemplate(template);
    setCurrentView('submissions');
    handleMenuClose();
  };

  const handleDeleteTemplate = async (template) => {
    if (!window.confirm(`Are you sure you want to delete "${template.title}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await apiRef.current.deleteCustomFormTemplate(template.id);
      setTemplates(templates.filter(t => t.id !== template.id));
      handleMenuClose();
    } catch (error) {
      console.error('Error deleting template:', error);
      alert('Error deleting template. Please try again.');
    }
  };

  const handleDuplicateTemplate = async (template) => {
    try {
      const duplicateData = {
        ...template,
        title: `${template.title} (Copy)`,
        id: undefined,
        created_at: undefined,
        updated_at: undefined
      };

      const newTemplate = await apiRef.current.createCustomFormTemplate(duplicateData);
      setTemplates([newTemplate, ...templates]);
      handleMenuClose();
    } catch (error) {
      console.error('Error duplicating template:', error);
      alert('Error duplicating template. Please try again.');
    }
  };

  const handleShareTemplate = (template) => {
    setSelectedTemplate(template);
    setShowSharingDialog(true);
    handleMenuClose();
  };

  // NEW: Save intake form handler
  const handleSaveIntakeForm = async (formData) => {
    try {
      if (selectedTemplate) {
        // Editing existing intake form
        const updatedTemplate = await apiRef.current.updateCustomFormTemplate(selectedTemplate.id, formData);
        setTemplates(templates.map(t => t.id === updatedTemplate.id ? updatedTemplate : t));
      } else {
        // Creating new intake form
        const newTemplate = await apiRef.current.createCustomFormTemplate(formData);
        setTemplates([newTemplate, ...templates]);
      }
      setCurrentView('dashboard');
      setSelectedTemplate(null);
    } catch (error) {
      console.error('Error saving intake form:', error);
      alert('Error saving form. Please try again.');
    }
  };

  const getSubmissionCount = (templateId) => {
    return submissions.filter(s => s.template === templateId).length;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderBreadcrumb = () => {
    const paths = {
      dashboard: 'Dashboard',
      create: 'Create Consent Form',
      'create-intake': 'Create Custom Inquiry Questions',
      edit: 'Edit Consent Form',
      'edit-intake': 'Edit Custom Inquiry Questions',
      fill: 'Fill Form',
      submissions: selectedTemplate ? `${selectedTemplate.title} Submissions` : 'All Submissions'
    };

    return (
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => setCurrentView('dashboard')}
          sx={{ textDecoration: 'none' }}
        >
          Custom Forms
        </Link>
        {currentView !== 'dashboard' && (
          <Typography color="text.primary">{paths[currentView]}</Typography>
        )}
      </Breadcrumbs>
    );
  };

  const renderSubscriptionAlert = () => {
    if (!hasEmailAccess) {
      return (
        <Alert 
          severity="info" 
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" startIcon={<Star />}>
              Upgrade
            </Button>
          }
        >
          <Typography variant="body2">
            <strong>Aftercare Email Feature:</strong> Upgrade to Standard or Growth Studio to send personalized aftercare instructions directly to your clients via email.
          </Typography>
        </Alert>
      );
    }
    return null;
  };

  const renderSharingChip = (template) => {
    if (!isStudioOwner) return null;

    if (template.studio_shareable) {
      return (
        <Chip icon={<Group />} label="Shared" size="small" color="success" variant="outlined" />
      );
    } else {
      return (
        <Chip icon={<Lock />} label="Private" size="small" color="default" variant="outlined" />
      );
    }
  };

  // UPDATED: Dashboard with tabs
  const renderDashboard = () => {
    const displayedTemplates = activeTab === 0 ? consentTemplates : intakeTemplates;
    
    return (
      <Box>
        {renderSubscriptionAlert()}
        
        {isStudioOwner && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Studio Owner:</strong> You can share your form templates with studio members. Use the sharing controls in the template menu.
            </Typography>
          </Alert>
        )}
        
        {/* Stats Grid */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>Total Forms</Typography>
                    <Typography variant="h4" fontWeight={600}>{templates.length}</Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'primary.main' }}><Assignment /></Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>Total Submissions</Typography>
                    <Typography variant="h4" fontWeight={600}>{submissions.length}</Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'success.main' }}><People /></Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>This Month</Typography>
                    <Typography variant="h4" fontWeight={600}>
                      {submissions.filter(s => {
                        const submissionDate = new Date(s.date_signed);
                        const now = new Date();
                        return submissionDate.getMonth() === now.getMonth() && 
                               submissionDate.getFullYear() === now.getFullYear();
                      }).length}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'info.main' }}><CalendarToday /></Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>Avg. Per Template</Typography>
                    <Typography variant="h4" fontWeight={600}>
                      {templates.length > 0 ? Math.round(submissions.length / templates.length) : 0}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'warning.main' }}><TrendingUp /></Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* NEW: Tabs for Consent vs Custom Inquiry Forms */}
        <Paper sx={{ mb: 3 }}>
          <Tabs 
            value={activeTab} 
            onChange={(e, newValue) => setActiveTab(newValue)}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            <Tab 
              icon={<Description />}
              iconPosition="start"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Consent Forms
                  <Chip label={consentTemplates.length} size="small" color="primary" />
                </Box>
              } 
            />
            <Tab 
              icon={<EventNote />}
              iconPosition="start"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Custom Inquiry Questions
                  <Chip label={intakeTemplates.length} size="small" color="secondary" />
                </Box>
              } 
            />
          </Tabs>
        </Paper>

        {/* Tab Description */}
        <Alert severity="info" sx={{ mb: 3 }}>
          {activeTab === 0 ? (
            <Typography variant="body2">
              <strong>Consent Forms</strong> include legal compliance sections, e-signature requirements, and ID uploads. 
              Use these for tattoo/piercing consent, liability waivers, and similar documents that staff fill out with clients.
            </Typography>
          ) : (
            <Typography variant="body2">
              <strong>Custom Inquiry Questions</strong> are additional fields that appear on your public inquiry form. 
              Create custom questions to collect style preferences, medical history, availability, or any other information from potential clients.
            </Typography>
          )}
        </Alert>

        {/* Header with Create Button */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" fontWeight={600}>
            {activeTab === 0 ? 'Consent Form Templates' : 'Custom Inquiry Templates'}
          </Typography>
          <Button variant="contained" startIcon={<Add />} onClick={handleCreateTemplate} size="large">
            Create {activeTab === 0 ? 'Consent Form' : 'Custom Questions'}
          </Button>
        </Box>

        {/* Template List - Filtered by tab */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : displayedTemplates.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <Assignment sx={{ fontSize: 80, color: 'action.disabled', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No {activeTab === 0 ? 'consent forms' : 'custom inquiry templates'} yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {activeTab === 0 
                ? 'Create consent forms for tattoo/piercing procedures with signatures and legal compliance' 
                : 'Create custom questions to add to your public inquiry form for potential clients'}
            </Typography>
            <Button variant="contained" startIcon={<Add />} onClick={handleCreateTemplate} size="large">
              Create Your First {activeTab === 0 ? 'Consent Form' : 'Custom Questions'}
            </Button>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {displayedTemplates.map((template) => (
              <Grid item xs={12} md={6} lg={4} key={template.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ flexGrow: 1, pr: 1 }}>
                        <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>{template.title}</Typography>
                        {template.is_shared && (
                          <Chip 
                            icon={<Group />} 
                            label={`Shared by ${template.user?.username || 'Studio'}`}
                            size="small" 
                            color="info" 
                            variant="outlined"
                            sx={{ mb: 1 }}
                          />
                        )}
                      </Box>
                      <IconButton size="small" onClick={(e) => handleMenuOpen(e, template)}>
                        <MoreVert />
                      </IconButton>
                    </Box>

                    {template.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {template.description.length > 100 
                          ? `${template.description.substring(0, 100)}...` 
                          : template.description}
                      </Typography>
                    )}

                    <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 0.5 }}>
                      {/* Form type chip */}
                      <Chip 
                        label={template.form_type === 'intake' ? 'Intake' : 'Consent'} 
                        size="small" 
                        color={template.form_type === 'intake' ? 'secondary' : 'primary'}
                        variant="filled"
                      />
                      {template.enable_email && (
                        <Chip icon={<Email />} label="Email" size="small" color="primary" variant="outlined" />
                      )}
                      {template.enable_id_upload && (
                        <Chip icon={<ImageIcon />} label="ID Required" size="small" color="success" variant="outlined" />
                      )}
                      <Chip label={`${template.form_config?.elements?.length || 0} Elements`} size="small" variant="outlined" />
                      {renderSharingChip(template)}
                    </Stack>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                      <Typography variant="body2" color="text.secondary">
                        {getSubmissionCount(template.id)} Submission{getSubmissionCount(template.id) !== 1 ? 's' : ''}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{formatDate(template.updated_at)}</Typography>
                    </Box>
                  </CardContent>
                  
                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Button fullWidth variant="contained" onClick={() => handleFillTemplate(template)}>
                      Fill Form
                    </Button>
                    <Button variant="outlined" onClick={() => handleViewSubmissions(template)} startIcon={<Visibility />}>
                      View ({getSubmissionCount(template.id)})
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    );
  };

  const renderSubmissionsList = () => {
    const filteredSubmissions = selectedTemplate 
      ? submissions.filter(s => s.template === selectedTemplate.id)
      : submissions;

    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h5" fontWeight={600}>
              {selectedTemplate ? selectedTemplate.title : 'All Form Submissions'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {filteredSubmissions.length} submission{filteredSubmissions.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
          {selectedTemplate && (
            <Button variant="contained" startIcon={<Add />} onClick={() => handleFillTemplate(selectedTemplate)}>
              New Submission
            </Button>
          )}
        </Box>

        {filteredSubmissions.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <People sx={{ fontSize: 80, color: 'action.disabled', mb: 2 }} />
            <Typography variant="h6" gutterBottom>No submissions yet</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {selectedTemplate 
                ? 'No one has filled out this form yet' 
                : 'No custom forms have been submitted yet'}
            </Typography>
            {selectedTemplate && (
              <Button variant="contained" onClick={() => handleFillTemplate(selectedTemplate)}>
                Fill This Form
              </Button>
            )}
          </Paper>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Client</TableCell>
                  {!selectedTemplate && <TableCell>Form Template</TableCell>}
                  <TableCell>Date Submitted</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSubmissions.map(submission => (
                  <TableRow key={submission.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{submission.client_name}</Typography>
                    </TableCell>
                    {!selectedTemplate && (
                      <TableCell>
                        <Typography variant="body2">{submission.template_title}</Typography>
                      </TableCell>
                    )}
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{formatDate(submission.date_signed)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Chip
                          icon={<CheckCircle />}
                          label={submission.is_valid ? 'Valid' : 'Invalid'}
                          size="small"
                          color={submission.is_valid ? 'success' : 'error'}
                          variant="outlined"
                        />
                        {submission.aftercare_email_sent && (
                          <Chip icon={<Email />} label="Email Sent" size="small" color="info" variant="outlined" />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      {hasEmailAccess && !submission.aftercare_email_sent && (
                        <Tooltip title="Send Aftercare Email">
                          <IconButton size="small" onClick={() => handleSendAftercareEmail(submission)} color="primary">
                            <Send />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', p: 3 }}>
      <Box sx={{ maxWidth: '1200px', mx: 'auto' }}>
        {renderBreadcrumb()}
        
        {currentView === 'dashboard' && renderDashboard()}
        
        {/* Consent Form Builder */}
        {(currentView === 'create' || currentView === 'edit') && (
          <CustomFormBuilder
            initialTemplate={selectedTemplate}
            onSave={async (formConfig) => {
              try {
                if (selectedTemplate) {
                  const updatedTemplate = await apiRef.current.updateCustomFormTemplate(
                    selectedTemplate.id, 
                    {
                      title: formConfig.metadata.title,
                      description: formConfig.metadata.description,
                      enable_email: formConfig.metadata.enableEmail,
                      enable_id_upload: formConfig.metadata.enableIdUpload,
                      form_type: 'consent',
                      form_config: {
                        elements: formConfig.elements,
                        metadata: formConfig.metadata
                      }
                    }
                  );
                  
                  setTemplates(templates.map(t => t.id === selectedTemplate.id ? updatedTemplate : t));
                  setSelectedTemplate(updatedTemplate);
                  
                } else {
                  const newTemplate = await apiRef.current.createCustomFormTemplate({
                    title: formConfig.metadata.title,
                    description: formConfig.metadata.description,
                    enable_email: formConfig.metadata.enableEmail,
                    enable_id_upload: formConfig.metadata.enableIdUpload,
                    form_type: 'consent',
                    form_config: {
                      elements: formConfig.elements,
                      metadata: formConfig.metadata
                    }
                  });
                  
                  setTemplates([newTemplate, ...templates]);
                  setSelectedTemplate(newTemplate);
                }
                
                setCurrentView('dashboard');
                
              } catch (error) {
                console.error('Error saving template:', error);
                alert('Error saving template. Please try again.');
              }
            }}
            onCancel={() => setCurrentView('dashboard')}
          />
        )}
        
        {/* NEW: Intake Form Builder */}
        {(currentView === 'create-intake' || currentView === 'edit-intake') && (
          <IntakeFormBuilder
            initialTemplate={selectedTemplate}
            onSave={handleSaveIntakeForm}
            onCancel={() => setCurrentView('dashboard')}
          />
        )}
        
        {currentView === 'fill' && selectedTemplate && (
          <CustomFormRenderer
            template={selectedTemplate}
            onSubmissionComplete={(submissionId) => {
              loadSubmissions();
              setCurrentView('dashboard');
            }}
          />
        )}
        
        {currentView === 'submissions' && renderSubmissionsList()}
      </Box>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={() => handleEditTemplate(menuTemplate)}>
          <Edit sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem onClick={() => handleDuplicateTemplate(menuTemplate)}>
          <FileCopy sx={{ mr: 1 }} /> Duplicate
        </MenuItem>
        {isStudioOwner && menuTemplate && !menuTemplate.is_shared && (
          <MenuItem onClick={() => handleShareTemplate(menuTemplate)}>
            <Share sx={{ mr: 1 }} /> Share Settings
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={() => handleDeleteTemplate(menuTemplate)} sx={{ color: 'error.main' }}>
          <Delete sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      <Dialog open={showSharingDialog} onClose={() => setShowSharingDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Share Template with Studio</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Share "{selectedTemplate?.title}" with your studio members
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="subtitle1">Share with Studio Members</Typography>
              <Typography variant="body2" color="text.secondary">
                Members with form viewing permissions will be able to see and use this template
              </Typography>
            </Box>
            <Switch
              checked={selectedTemplate?.studio_shareable || false}
              onChange={(e) => handleToggleSharing(selectedTemplate, e.target.checked)}
              disabled={sharingTemplate === selectedTemplate?.id}
            />
          </Box>
          
          {sharingTemplate === selectedTemplate?.id && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              <Typography variant="body2">Updating sharing settings...</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSharingDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Fab
        color="primary"
        aria-label="add"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={handleCreateTemplate}
      >
        <Add />
      </Fab>

      <Snackbar open={emailSuccess} autoHideDuration={6000} onClose={() => setEmailSuccess(false)}>
        <Alert severity="success" onClose={() => setEmailSuccess(false)}>
          Aftercare email sent successfully!
        </Alert>
      </Snackbar>

      <Snackbar open={!!emailError} autoHideDuration={6000} onClose={() => setEmailError('')}>
        <Alert severity="error" onClose={() => setEmailError('')}>{emailError}</Alert>
      </Snackbar>

      <Dialog open={showAftercareDialog} onClose={() => setShowAftercareDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Aftercare Instructions</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Aftercare Instructions"
            value={aftercareInstructions}
            onChange={(e) => setAftercareInstructions(e.target.value)}
            placeholder="Enter custom aftercare instructions or leave blank for default..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAftercareDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={() => setShowAftercareDialog(false)}
            disabled={sendingEmail}
          >
            {sendingEmail ? <CircularProgress size={20} /> : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomFormManager;